import {
  BIRDModelController,
  PositionedModelController,
  SectionController,
  VisualElementController,
} from '@sas-dvr/ltjs-bird';
import { last, share, take, takeUntil, mergeMap } from 'rxjs/operators';
import { fromUnload, getKey, withRetained } from '../util';
import { BirdEpic } from './BirdEpic';
import { AnyAction } from 'redux';
import { EMPTY, Subject } from 'rxjs';
import { ofActionCreatorType } from '../util/rxUtils';
import { EpicActions } from '../actions/epicActions';
import { getMediaContainerController } from '../util/controllerUtils';
import { ltjsModule } from '@sas-dvr/ltjs-commons/ltjsEnvironment';

export const loadBirdModelEpic: BirdEpic = (actions$, state$, deps) => {
  // Each model gets a separate action observable for better performance
  const actionSubjects = new Map<String, Subject<AnyAction>>();

  actions$.subscribe((action) => {
    let id = action.payload?.id;
    if (!id && action.type === EpicActions.actionTypes.UnloadBirdModel) {
      id = getActionKey(action.payload);
    }
    const subject = actionSubjects.get(id);
    if (subject) {
      // Send actions with ids to the matching loader
      subject.next(action);
    } else if (!id) {
      // Broadcast actions without ids (e.g. SectionState.actions.notifyLinkNavigation) to all loaders
      for (const subject of actionSubjects.values()) {
        subject.next(action);
      }
    }
  });

  return actions$.pipe(
    ofActionCreatorType<typeof EpicActions.actions.loadBirdModel>(
      EpicActions.actionTypes.LoadBirdModel
    ),
    mergeMap((action) => {
      const controller = action.payload;
      const key = getActionKey(controller);
      if (actionSubjects.get(key)) {
        console.error(`Controller with key '${key}' already loaded`);
        return EMPTY;
      }

      const actionSubject = new Subject<AnyAction>();
      actionSubjects.set(key, actionSubject);
      const myActions$ = actionSubject.asObservable();

      // Create a shared unload observable that can be used by the loader, as
      // well as the condition for completion of the entire chain.
      const unload$ = fromUnload(controller, myActions$).pipe(take(1), share());
      unload$.subscribe(() => {
        const key = getActionKey(action.payload);
        actionSubjects.delete(key);
      });

      let load$;
      if (controller instanceof VisualElementController) {
        // This should only happen for visual element that don't have visual controllers, like supplemental visuals
        const loader = deps.getLoaderForVisualElementController(controller);
        load$ = loader(null, controller, unload$, myActions$, state$, deps);
      } else {
        const loader = deps.getLoaderForController(controller);
        load$ = loader(controller, unload$, myActions$, state$, deps);
      }

      return load$.pipe(
        withRetained(controller),
        // Use the 'last' operator in the takeUntil condition to let the loader's
        // unload$ subscriptions next before the whole chain is completed.
        takeUntil(unload$.pipe(last()))
      );
    })
  );
};

// getKey for any BIRDModelController
export function getActionKey(controller: BIRDModelController) {
  if (controller instanceof SectionController) {
    // loadSectionController needs the same actions as loadMediaContainerController
    controller = getMediaContainerController(controller) ?? controller;
  }
  if (
    controller instanceof PositionedModelController ||
    controller instanceof VisualElementController
  ) {
    return getKey(controller);
  }
  // Same fallback as getKey. Unstable if the wrapper is released
  return ltjsModule.getClosetKey(controller.getClosetAddress()) + '';
}
